import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Field,
  Input,
  NativeSelect,
} from '@provenance/design';
import type { SubmitUpload, UploadSession, UploadStatusRow, UploadSubmissionResult } from '@provenance/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, Upload as UploadIcon } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PublicApiError, publicRequest } from '../api';

/** Today in the `yyyy-mm-dd` an <input type="date"> expects. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Reading a File as base64 without the data: prefix the API would reject. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Where the magic link lands — spec v4 §25.4.
 *
 * The session opens once, from a single-use token, and lasts an hour. A lapsed signatory is
 * told so **here** and not on the email form: they have proved they control the address, so
 * naming their authorisation state is no longer an information leak (§25.3).
 */
export function Upload() {
  const { token = '' } = useParams();

  const session = useQuery({
    queryKey: ['upload-session', token],
    queryFn: () => publicRequest<UploadSession>(`/api/v1/public/upload/session/${token}`),
    retry: false,
    // Single-use: refetching would spend a token that has already been exchanged.
    staleTime: Infinity,
    refetchOnMount: false,
  });

  if (session.isLoading) {
    return <p className="mx-auto max-w-2xl px-4 py-16 [font-size:14px]">Opening your link…</p>;
  }

  if (session.isError) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="That link cannot be used">
          Upload links can be used once and expire after 15 minutes. Ask for a new one from
          your chamber's submission page.
        </Callout>
      </div>
    );
  }

  return <UploadForm session={session.data!} />;
}

/** One supporting document chosen on the form, before it is sent. */
interface Attachment {
  file: File;
  category: string;
  /** Asks the chamber to stamp this file as well as the certificate. */
  stamp: boolean;
}

function UploadForm({ session }: { session: UploadSession }) {
  const [documentTypeId, setDocumentTypeId] = useState(session.documentTypes[0]?.id ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [senderReference, setSenderReference] = useState('');
  // An amendment names the certificate it amends (D-029); blank means an original.
  const [amending, setAmending] = useState(false);
  const [originalCertificateNo, setOriginalCertificateNo] = useState('');
  const [neededBy, setNeededBy] = useState(today());
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceTotal, setInvoiceTotal] = useState('');
  const [destination, setDestination] = useState('');
  /**
   * The supporting documents to send. Held as a list rather than a single input because the
   * common case is two — a commercial invoice and a packing list — and the chamber's rules
   * decide which of them it will otherwise have to ask for.
   */
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadSubmissionResult | null>(null);
  const liveRef = useRef<HTMLParagraphElement>(null);

  /**
   * Issued once per opened form and spent on submit. A double-click, a browser retry or an
   * impatient second press all carry the same token, and the second one is collapsed onto
   * the first rather than creating a twin certificate request (T-089).
   */
  const selectedType = session.documentTypes.find((d) => d.id === documentTypeId);

  const submissionToken = useMemo(
    () => `${session.sessionToken.slice(0, 8)}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    [session.sessionToken],
  );

  const status = useQuery({
    queryKey: ['upload-status', session.sessionToken],
    queryFn: () =>
      publicRequest<UploadStatusRow[]>('/api/v1/public/upload/status', {
        headers: { 'x-upload-session': session.sessionToken },
      }),
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const body: SubmitUpload = {
        documentTypeId,
        documentFile: { filename: file.name, contentBase64: await toBase64(file) },
        ...(attachments.length > 0
          ? {
              supportingDocuments: await Promise.all(
                attachments.map(async (a) => ({
                  filename: a.file.name,
                  contentBase64: await toBase64(a.file),
                  category: a.category,
                  requestSignatureAndStamp: a.stamp,
                })),
              ),
            }
          : {}),
        ...(senderReference.trim() ? { senderReference: senderReference.trim() } : {}),
        ...(amending && originalCertificateNo.trim() ? { originalCertificateNo: originalCertificateNo.trim() } : {}),
        ...(neededBy ? { requestedTargetDate: neededBy } : {}),
        keyFields: {
          ...(invoiceNumber ? { invoiceNumber } : {}),
          ...(invoiceTotal ? { invoiceTotal } : {}),
          ...(destination ? { destinationCountry: destination.toUpperCase() } : {}),
        },
        submissionToken,
      };
      const res = await publicRequest<UploadSubmissionResult>('/api/v1/public/upload/submit', {
        method: 'POST',
        body,
        headers: { 'x-upload-session': session.sessionToken },
      });
      setResult(res);
      setAttachments([]);
      void status.refetch();
    } catch (err) {
      setError(err instanceof PublicApiError ? err.message : 'The upload did not go through.');
    } finally {
      setBusy(false);
    }
  }

  if (session.lapsed) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <Callout
          tone="warn"
          icon={<Clock className="size-4" />}
          title="Your authorisation has lapsed"
        >
          {/* Said here, never on the email form — the exporter has proved they control this
              address, so naming their state is no longer a disclosure. */}
          The specimen signature {session.chamberName} holds for {session.signatoryName} is no
          longer current
          {session.lapsedOn
            ? `, as of ${new Date(session.lapsedOn).toLocaleDateString('en-AU')}`
            : ''}
          . Contact {session.chamberName} to renew it, and you will be able to upload again.
        </Callout>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:py-16">
      <header className="mb-8">
        <p className="[font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">
          {session.chamberName}
        </p>
        <h1 className="mt-1 [font-size:var(--pv-text-xl)] font-semibold">
          Upload a certificate.
        </h1>
        <p className="mt-2 [font-size:14px] text-[var(--pv-muted)]">
          Signed in as {session.signatoryName} for {session.organisationName}.
        </p>
      </header>

      {result ? (
        <Callout
          tone={result.requiredDocuments && !result.requiredDocuments.satisfied ? 'warn' : 'ok'}
          icon={<CheckCircle2 className="size-4" />}
          title="We have your certificate"
        >
          {result.duplicate
            ? `We already had this submission, so nothing was duplicated. Your reference is ${result.senderReference}.`
            : `Your reference is ${result.senderReference}. ${session.chamberName} will review it and email you the outcome.`}
          {result.requiredDocuments && result.requiredDocuments.missing.length > 0 && (
            // Said on the confirmation as well as in the email. The submission has already
            // succeeded — this is the chamber telling them what it will otherwise have to
            // ask for, which is cheaper for everyone than a clarification two days later.
            <p className="mt-2">
              {session.chamberName} usually also needs{' '}
              {result.requiredDocuments.missing.map((m) => m.categoryLabel.toLowerCase()).join(' and ')}.
              Your certificate is in the queue either way — send them to your chamber, or wait to be
              asked.
            </p>
          )}
          <div className="mt-3">
            <Button variant="secondary" size="sm" onClick={() => setResult(null)}>
              Upload another
            </Button>
          </div>
        </Callout>
      ) : (
        <Card>
          <CardHeader
            title="The signed certificate"
            description="A PDF of the completed, signed form."
          />
          <form onSubmit={submit} className="flex flex-col gap-4 p-4" noValidate>
            <Field label="Which certificate is this?" required>
              <NativeSelect
                value={documentTypeId}
                onChange={(e) => setDocumentTypeId(e.target.value)}
              >
                {session.documentTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>

            <div className="flex flex-col gap-3 rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] p-3">
              <label className="flex cursor-pointer items-start gap-2.5 [font-size:13.5px]">
                <input
                  type="checkbox"
                  checked={amending}
                  onChange={(e) => setAmending(e.target.checked)}
                  className="mt-0.5 size-4 accent-[var(--pv-brand)]"
                />
                <span>
                  <span className="font-medium">This amends a certificate already issued</span>
                  <span className="block text-[var(--pv-muted)]">
                    The amended certificate keeps the original number with a suffix, and replaces it.
                  </span>
                </span>
              </label>
              {amending && (
                <Field
                  label="Certificate number being amended"
                  required
                  help={`As printed on the certificate. ${session.chamberName} allows a limited number of amendments per certificate; after that, apply for a new one.`}
                >
                  <Input
                    value={originalCertificateNo}
                    maxLength={64}
                    onChange={(e) => setOriginalCertificateNo(e.target.value)}
                    placeholder="e.g. BCQ-000123"
                    className="pv-mono"
                  />
                </Field>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Your reference"
                help="Optional. Your own job or order number, if you use one — we will show it back to you and your chamber. Leave blank and we will allocate one."
              >
                <Input
                  value={senderReference}
                  maxLength={64}
                  onChange={(e) => setSenderReference(e.target.value)}
                  placeholder="e.g. JOB-2026-114"
                />
              </Field>
              <Field
                label="When do you need it?"
                help="Today unless you choose otherwise."
              >
                <Input
                  type="date"
                  value={neededBy}
                  min={today()}
                  onChange={(e) => setNeededBy(e.target.value)}
                />
              </Field>
            </div>

            <Field
              label="The signed PDF"
              required
              error={error ?? undefined}
              help="Up to 20MB. It must be the signed form, not a scan of a covering letter."
            >
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setError(null);
                }}
              />
            </Field>

            {/* Optional by decision (Q4). These restore a second side to reconcile the PDF
                against, but a typo here must never be able to block a certificate — the
                back end treats a mismatch as a warning, never a failure. */}
            <fieldset className="rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-3">
              <legend className="px-1 [font-size:12.5px] text-[var(--pv-muted)]">
                Optional — helps us check the certificate
              </legend>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Invoice number">
                  <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                </Field>
                <Field label="Invoice total">
                  <Input value={invoiceTotal} onChange={(e) => setInvoiceTotal(e.target.value)} />
                </Field>
                <Field label="Destination" help="Two-letter country code.">
                  <Input
                    value={destination}
                    maxLength={2}
                    onChange={(e) => setDestination(e.target.value.toUpperCase())}
                    placeholder="NZ"
                  />
                </Field>
              </div>
            </fieldset>

            <SupportingDocuments
              chamberName={session.chamberName}
              categories={session.supportingCategories}
              rules={selectedType?.requiredDocuments ?? []}
              attachments={attachments}
              onChange={setAttachments}
            />

            <p ref={liveRef} aria-live="polite" className="sr-only">
              {busy ? 'Uploading your certificate' : ''}
            </p>

            <div>
              <Button
                type="submit"
                variant="primary"
                loading={busy}
                disabled={!file || !documentTypeId || (amending && !originalCertificateNo.trim())}
                leadingIcon={<UploadIcon className="size-3.5" />}
              >
                {/* Deliberately does NOT interpolate the chamber name. Buttons are
                    whitespace-nowrap by design, and a chamber with a long legal name —
                    "Business Chamber Queensland" already overflows 320px — would push the
                    page into horizontal scroll on a phone. The chamber is named twice
                    above this: in the page eyebrow and in "Signed in as … for …". */}
                Send to your chamber
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader title="Your recent uploads" />
        <div className="flex flex-col divide-y divide-[var(--pv-line)]">
          {(status.data ?? []).length === 0 && (
            <p className="px-4 py-6 [font-size:13px] text-[var(--pv-muted)]">
              Nothing uploaded yet.
            </p>
          )}
          {(status.data ?? []).map((row) => (
            <div key={row.docId} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="pv-mono [font-size:12.5px] font-medium">{row.senderReference}</p>
                <p className="mt-0.5 [font-size:12.5px] text-[var(--pv-muted)]">
                  {row.documentTypeName} · {new Date(row.receivedAt).toLocaleDateString('en-AU')}
                </p>
                {row.outcomeNote && (
                  <p className="mt-1 [font-size:12.5px] text-[var(--pv-muted)]">{row.outcomeNote}</p>
                )}
              </div>
              <Badge tone={row.certificateNo ? 'ok' : 'neutral'} size="sm">
                {row.certificateNo ?? row.status.replace(/_/g, ' ').toLowerCase()}
              </Badge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/**
 * Choosing what to send alongside the certificate.
 *
 * The checklist is the point. A chamber that has said what it needs for this certificate
 * type has the list shown here **before** anything is sent, so the common case — a missing
 * commercial invoice discovered two days later by a clarification email — stops happening.
 *
 * Two rules the wording has to carry:
 *
 *  • **Nothing here blocks the submission.** An exporter who cannot produce a packing list
 *    today can still lodge; the chamber will ask. Saying so plainly stops someone abandoning
 *    a lodgement they were entitled to make.
 *  • **A chamber with no rules shows no checklist at all.** An empty list means the chamber
 *    has no opinion, not that nothing is wanted, and an empty box headed "required
 *    documents" would read as the opposite.
 */
function SupportingDocuments({
  chamberName,
  categories,
  rules,
  attachments,
  onChange,
}: {
  chamberName: string;
  categories: Array<{ code: string; label: string }>;
  rules: Array<{
    category: string;
    categoryLabel: string;
    required: boolean;
    stampByDefault: boolean;
    note: string | null;
  }>;
  attachments: Attachment[];
  onChange: (next: Attachment[]) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const chosen = new Set(attachments.map((a) => a.category));
  const missing = rules.filter((r) => r.required && !chosen.has(r.category));

  /** The category to pre-select: the first required one still outstanding. */
  const nextCategory = () =>
    missing[0]?.category ?? rules.find((r) => !chosen.has(r.category))?.category ?? categories[0]?.code ?? 'OTHER';

  const add = (files: FileList | null) => {
    if (!files) return;
    const next = [...attachments];
    for (const file of Array.from(files)) {
      const category = rules.find((r) => r.required && !next.some((a) => a.category === r.category))?.category
        ?? nextCategory();
      next.push({
        file,
        category,
        // Pre-ticked from the chamber's rule, because whether an invoice needs stamping is
        // a destination rule the exporter should not have to know.
        stamp: rules.find((r) => r.category === category)?.stampByDefault ?? false,
      });
    }
    onChange(next);
  };

  const patch = (index: number, change: Partial<Attachment>) =>
    onChange(attachments.map((a, i) => (i === index ? { ...a, ...change } : a)));

  return (
    <fieldset className="rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-3">
      <legend className="px-1 [font-size:12.5px] text-[var(--pv-muted)]">
        Supporting documents
      </legend>

      {rules.length > 0 && (
        <div className="mb-3 rounded-[var(--pv-radius-sm)] bg-[var(--pv-card-alt)] px-3 py-2.5">
          <p className="[font-size:12.5px] font-medium">
            {chamberName} usually needs these with this certificate
          </p>
          <ul className="mt-1.5 flex list-none flex-col gap-1 p-0">
            {rules.map((rule) => {
              const have = chosen.has(rule.category);
              return (
                <li key={rule.category} className="flex items-start gap-2 [font-size:12.5px]">
                  <span
                    aria-hidden
                    className={
                      have
                        ? 'mt-1.5 size-2 shrink-0 rounded-full bg-[var(--pv-leaf-deep)]'
                        : rule.required
                          ? 'mt-1.5 size-2 shrink-0 rounded-full bg-[var(--pv-clay-deep)]'
                          : 'mt-1.5 size-2 shrink-0 rounded-full bg-[var(--pv-line-strong)]'
                    }
                  />
                  <span className={have ? 'text-[var(--pv-muted)] line-through' : ''}>
                    {rule.categoryLabel}
                    {!rule.required && <span className="text-[var(--pv-faint)]"> — optional</span>}
                    {rule.stampByDefault && (
                      <span className="text-[var(--pv-faint)]"> — normally stamped too</span>
                    )}
                    {rule.note && <span className="block text-[var(--pv-faint)]">{rule.note}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 [font-size:12px] text-[var(--pv-faint)]">
            {missing.length === 0
              ? 'You have attached everything they usually ask for.'
              : 'You can still send without them — your chamber will ask if they need them.'}
          </p>
        </div>
      )}

      {attachments.length > 0 && (
        <ul className="mb-3 flex list-none flex-col gap-2 p-0">
          {attachments.map((a, index) => (
            <li
              key={`${a.file.name}-${index}`}
              className="flex flex-wrap items-center gap-2 rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] px-3 py-2"
            >
              <span className="min-w-0 flex-1 truncate [font-size:12.5px]">
                {a.file.name}
                <span className="text-[var(--pv-faint)]"> · {Math.round(a.file.size / 1024)} KB</span>
              </span>

              <label className="sr-only" htmlFor={`cat-${index}`}>
                What is {a.file.name}?
              </label>
              <select
                id={`cat-${index}`}
                className="rounded-[var(--pv-radius-sm)] border border-[var(--pv-line-strong)] bg-white px-2 py-1 [font-size:12.5px]"
                value={a.category}
                onChange={(e) =>
                  patch(index, {
                    category: e.target.value,
                    stamp: rules.find((r) => r.category === e.target.value)?.stampByDefault ?? a.stamp,
                  })
                }
              >
                {categories.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>

              <label className="flex items-center gap-1.5 [font-size:12.5px] text-[var(--pv-muted)]">
                <input
                  type="checkbox"
                  checked={a.stamp}
                  onChange={(e) => patch(index, { stamp: e.target.checked })}
                />
                Stamp this too
              </label>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(attachments.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={fileInput}
        type="file"
        multiple
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => {
          add(e.target.files);
          // Cleared so choosing the same file twice still fires a change.
          e.target.value = '';
        }}
      />
      <Button type="button" variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
        Attach a document
      </Button>
      <p className="mt-2 [font-size:12px] text-[var(--pv-faint)]">
        PDFs only, up to 10 files. These are optional — your certificate will be lodged either way.
      </p>
    </fieldset>
  );
}
